import { slugify } from "./slug";
import { normalizeQuestion, recomputeTopic } from "./merge";

// Keep the longer note (a crude but safe "don't lose what I wrote" rule).
function pickNotes(a, b) {
  a = a || "";
  b = b || "";
  if (a && b) return a.length >= b.length ? a : b;
  return a || b;
}

// Keep the earliest solved timestamp when both sides have one.
function pickSolvedAt(a, b) {
  if (a && b) return a < b ? a : b;
  return a || b || null;
}

// Keep the more-advanced spaced-rep schedule (later next-review date wins).
function pickReviseAt(a, b) {
  if (a && b) return a >= b ? a : b;
  return a || b || null;
}

// Union two saved states for the same problem. Booleans are OR-ed so progress
// is never lost, strings prefer the non-empty / richer value.
function mergeQuestion(a, b, topicName) {
  const x = normalizeQuestion(a, topicName);
  const y = normalizeQuestion(b, topicName);
  return {
    Topic: x.Topic || y.Topic,
    Problem: x.Problem || y.Problem,
    Done: x.Done || y.Done,
    Bookmark: x.Bookmark || y.Bookmark,
    Notes: pickNotes(x.Notes, y.Notes),
    Revise: x.Revise || y.Revise,
    Difficulty: x.Difficulty || y.Difficulty,
    SolvedAt: pickSolvedAt(x.SolvedAt, y.SolvedAt),
    TimeSpent: Math.max(x.TimeSpent || 0, y.TimeSpent || 0),
    ReviseAt: pickReviseAt(x.ReviseAt, y.ReviseAt),
    ReviseStage: Math.max(x.ReviseStage || 0, y.ReviseStage || 0),
    URL: x.URL || y.URL,
    URL2: x.URL2 || y.URL2,
    Custom: x.Custom || y.Custom,
  };
}

// Symmetric, non-destructive union of the local list and the cloud copy.
//
// Why not reuse mergeTopicData? That helper treats the second argument as an
// overlay and will happily write `Done:false` over `Done:true`. For syncing two
// real user datasets we must OR the progress, never clobber it — that's what
// this does. Canonical (seed) questions line up by index across both sides;
// custom problems that only exist on one side are appended (matched by title so
// nothing is dropped on an index collision). Order follows `local`, then any
// topics that only exist in `cloud`.
export function cloudMerge(local, cloud) {
  if (!cloud || cloud.length === 0) return (local || []).slice();
  if (!local || local.length === 0) return (cloud || []).slice();

  const cloudBySlug = new Map();
  cloud.forEach((t) => {
    if (t && t.topicName != null) cloudBySlug.set(slugify(t.topicName), t);
  });

  const seen = new Set();
  const out = [];

  local.forEach((lt) => {
    const slug = slugify(lt.topicName);
    seen.add(slug);
    const ct = cloudBySlug.get(slug);
    const lq = lt.questions || [];
    const cq = (ct && ct.questions) || [];
    const len = Math.max(lq.length, cq.length);

    const questions = [];
    for (let i = 0; i < len; i++) {
      questions.push(mergeQuestion(lq[i], cq[i], lt.topicName));
    }

    // Safety net: append any cloud problem whose title isn't already present
    // (covers two devices appending different custom problems at the same index).
    const haveTitles = new Set(questions.map((q) => (q.Problem || "").toLowerCase()));
    cq.forEach((q) => {
      const title = (q && q.Problem ? q.Problem : "").toLowerCase();
      if (title && !haveTitles.has(title)) {
        questions.push(normalizeQuestion({ ...q, Custom: true }, lt.topicName));
        haveTitles.add(title);
      }
    });

    out.push(
      recomputeTopic({
        topicName: lt.topicName,
        position: lt.position,
        started: lt.started,
        Custom: !!lt.Custom || !!(ct && ct.Custom),
        questions,
      })
    );
  });

  // topics that only exist in the cloud
  cloud.forEach((ct) => {
    if (!ct || ct.topicName == null) return;
    const slug = slugify(ct.topicName);
    if (seen.has(slug)) return;
    seen.add(slug);
    out.push(recomputeTopic({ ...ct, Custom: true }));
  });

  out.forEach((t, i) => {
    t.position = i;
  });

  return out;
}

export default cloudMerge;
